import React, { useEffect, useState } from 'react';
import {
  Activity,
  BookOpen,
  Calendar,
  FileText,
  Plus,
  User
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import { bookletService } from '../../services/bookletService';
import DocumentList from '../Documents/DocumentList';

const Dashboard = () => {
  const { user, userProfile } = useAuth();
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalSize: 0,
    thisMonth: 0,
    pdfCount: 0,
    totalBooklets: 0,
    publishedBooklets: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const loadStats = async () => {
      try {
        setLoading(true);
        const [documents, booklets] = await Promise.all([
          documentService.getUserDocuments(user.uid),
          bookletService.getUserBooklets(user.uid)
        ]);

        const now = new Date();
        const thisMonth = documents.filter((doc) => {
          const docDate = doc.createdAt?.toDate?.() || new Date(doc.createdAt);
          return docDate.getMonth() === now.getMonth()
            && docDate.getFullYear() === now.getFullYear();
        }).length;

        const publishedBooklets = booklets.filter((booklet) => booklet.status === 'published').length;

        setStats({
          totalDocuments: documents.length,
          totalSize: documents.reduce((total, doc) => total + (doc.size || 0), 0),
          thisMonth,
          pdfCount: documents.filter((doc) => `${doc.type || ''}`.includes('pdf')).length,
          totalBooklets: booklets.length,
          publishedBooklets
        });
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user?.uid]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserName = () => (
    userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'there'
  );

  return (
    <div className="space-y-8">
      <div className="rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-2xl font-bold">
              {getGreeting()}, {getUserName()}!
            </h1>
            <p className="text-indigo-100">
              Welcome back to ParrotDocs. Manage your documents and create interactive booklets.
            </p>
          </div>
          <div className="hidden space-x-3 md:flex">
            <Link
              to="/doc-tools"
              className="rounded-lg bg-white bg-opacity-20 px-4 py-2 font-medium text-white transition-colors backdrop-blur-sm hover:bg-opacity-30"
            >
              Doc Tools
            </Link>
            <Link
              to="/booklet/create"
              className="rounded-lg bg-white bg-opacity-20 px-4 py-2 font-medium text-white transition-colors backdrop-blur-sm hover:bg-opacity-30"
            >
              Create Booklet
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="rounded-full bg-blue-100 p-3">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.totalDocuments}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="rounded-full bg-purple-100 p-3">
              <BookOpen className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Booklets</p>
              <p className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.totalBooklets}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="rounded-full bg-green-100 p-3">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.thisMonth}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="rounded-full bg-orange-100 p-3">
              <Activity className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Storage Used</p>
              <p className="text-2xl font-bold text-gray-900">{loading ? '...' : formatFileSize(stats.totalSize)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <Link
          to="/doc-tools"
          className="group rounded-lg border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div className="flex items-center space-x-4">
            <div className="rounded-full bg-indigo-100 p-3 transition-colors group-hover:bg-indigo-200">
              <FileText className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Doc Tools</h3>
              <p className="text-sm text-gray-600">PDF utilities and image canvas</p>
            </div>
          </div>
        </Link>

        <Link
          to="/booklets"
          className="group rounded-lg border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div className="flex items-center space-x-4">
            <div className="rounded-full bg-purple-100 p-3 transition-colors group-hover:bg-purple-200">
              <BookOpen className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Booklets</h3>
              <p className="text-sm text-gray-600">Interactive flipbooks</p>
            </div>
          </div>
        </Link>

        <Link
          to="/booklet/create"
          className="group rounded-lg border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div className="flex items-center space-x-4">
            <div className="rounded-full bg-green-100 p-3 transition-colors group-hover:bg-green-200">
              <Plus className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Create Booklet</h3>
              <p className="text-sm text-gray-600">From PDF to flipbook</p>
            </div>
          </div>
        </Link>

        <Link
          to="/settings"
          className="group rounded-lg border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div className="flex items-center space-x-4">
            <div className="rounded-full bg-orange-100 p-3 transition-colors group-hover:bg-orange-200">
              <User className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Settings</h3>
              <p className="text-sm text-gray-600">Manage preferences</p>
            </div>
          </div>
        </Link>
      </div>

      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <div className="flex space-x-2">
            <span className="text-sm text-gray-600">
              {stats.publishedBooklets} published booklet{stats.publishedBooklets !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h4 className="mb-3 font-medium text-gray-900">Recent Documents</h4>
            <div className="space-y-2">
              <div className="text-sm text-gray-600">{stats.pdfCount} PDF files uploaded</div>
              <div className="text-sm text-gray-600">{stats.thisMonth} documents this month</div>
            </div>
          </div>

          <div>
            <h4 className="mb-3 font-medium text-gray-900">Booklet Stats</h4>
            <div className="space-y-2">
              <div className="text-sm text-gray-600">{stats.totalBooklets} total booklets</div>
              <div className="text-sm text-gray-600">{stats.publishedBooklets} published booklets</div>
            </div>
          </div>
        </div>
      </div>

      <DocumentList />
    </div>
  );
};

export default Dashboard;
